import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function usePostLikes(postId: string) {
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Load initial state
  useEffect(() => {
    if (user && postId) {
      loadLikeStatus();
    }
  }, [user, postId]);

  const loadLikeStatus = async () => {
    try {
      // Check if user liked
      const { data: userLike } = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', user?.id)
        .maybeSingle();

      setIsLiked(!!userLike);

      // Count total likes
      const { count } = await supabase
        .from('post_likes')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId);

      setLikeCount(count || 0);
    } catch (error) {
      console.error('Error loading like status:', error);
    }
  };

  const toggleLike = async () => {
    if (!user) {
      // Redirect to login
      window.location.href = '/?tab=signin';
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('toggle_post_like', {
        post_uuid: postId
      });

      if (error) throw error;

      // Update local state
      setIsLiked(data);
      setLikeCount(prev => data ? prev + 1 : prev - 1);
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    isLiked,
    likeCount,
    loading,
    toggleLike,
    canLike: !!user
  };
} 